import { GameState } from '../types/game';
import { logger } from './logger';

// Lokalne przechowywanie stanu gry w trybie REAL (snapshot + preferencje).
// Online nie zapisuje snapshotu – jedynie cache per-game wydarzeń/ID w localStorage.

const GAME_STORAGE_KEY = 'dice1k-game-state';
export const GAME_ID_KEY = 'dice1k-current-game-id';
const MODE_STORAGE_KEY = 'dice1k-mode';
const THEME_STORAGE_KEY = 'dice1k_theme';
const LANGUAGE_STORAGE_KEY = 'dice1k-language';
const EMOTE_STORAGE_KEY = 'dice1k_emote_slots';
const SOUND_STORAGE_KEY = 'dice1k-sound-enabled';
const HAPTIC_STORAGE_KEY = 'dice1k-haptics-enabled';
const EVENTS_CACHE_PREFIX = 'dice1k_persisted_events_';
const PLAYER_ID_PREFIX = 'dice1k_last_player_id_';
const LAST_ONLINE_GAME_KEY = 'dice1k_last_online_game';
const GROUP_TOKEN_KEY = 'dice1k_group_token';
const GROUP_LAST_GAME_KEY = 'dice1k_group_last_game';
const PER_GAME_PREFIXES = [EVENTS_CACHE_PREFIX, PLAYER_ID_PREFIX];
const PERSIST_KEYS = new Set([
  THEME_STORAGE_KEY,
  LANGUAGE_STORAGE_KEY,
  EMOTE_STORAGE_KEY,
  SOUND_STORAGE_KEY,
  HAPTIC_STORAGE_KEY,
  GROUP_TOKEN_KEY,
  GROUP_LAST_GAME_KEY,
]);

type PersistedScoreEntry = Omit<GameState['history'][number], 'timestamp'> & { timestamp: string | Date | number };

const normalizeHistory = (history: unknown): GameState['history'] => {
  if (!Array.isArray(history)) return [];
  return history.map((entry) => {
    const candidate = entry as PersistedScoreEntry;
    return {
      ...candidate,
      timestamp: new Date(candidate.timestamp),
    };
  });
};

export function clearAllDiceKeys(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (PERSIST_KEYS.has(key)) continue; // nie usuwaj preferencji języka i motywu
      if (key.startsWith('dice1k')) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch (error) {
    logger.error('GAME_PERSISTENCE_CLEAR_ALL_KEYS_FAILED', error);
  }
}

export function saveGameState(gameState: GameState): void {
  // W testach (Node) localStorage nie istnieje – po prostu pomijamy zapis
  if (typeof localStorage === 'undefined') return;
  try {
    if (gameState.mode === 'ONLINE') return; // tryb online nie zapisuje snapshotu lokalnie
    prunePerGameKeys(gameState.id); // usuń cache innych gier
    const toSave = { ...gameState, mode: 'REAL' as const };
    localStorage.setItem(GAME_STORAGE_KEY, JSON.stringify(toSave));
    localStorage.setItem(GAME_ID_KEY, gameState.id);
    localStorage.setItem(MODE_STORAGE_KEY, 'REAL');
  } catch (error) {
    logger.error('GAME_PERSISTENCE_SAVE_FAILED', error);
  }
}

export function loadGameState(): GameState | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const saved = localStorage.getItem(GAME_STORAGE_KEY);
    if (!saved) return null;
    const raw = JSON.parse(saved) as Partial<GameState> & Record<string, unknown>;
    const savedMode = (localStorage.getItem(MODE_STORAGE_KEY) as GameState['mode'] | null) || null;
    raw.history = normalizeHistory(raw.history);
    const modeString = (raw.mode as GameState['mode']) || savedMode || 'REAL';
    if (modeString !== 'REAL') return null; // snapshoty lokalne tylko dla REAL
    raw.mode = 'REAL';
    localStorage.setItem(MODE_STORAGE_KEY, 'REAL');
    if (raw.lobby && typeof raw.lobby !== 'object') raw.lobby = undefined;
    if (raw.turnCycle && typeof raw.turnCycle !== 'object') raw.turnCycle = undefined;
    return raw as GameState;
  } catch (error) {
    logger.error('GAME_PERSISTENCE_LOAD_FAILED', error);
    return null;
  }
}

export function clearGameState(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const existingId = localStorage.getItem(GAME_ID_KEY) || undefined;
    clearPerGameKeys(existingId || undefined);
    localStorage.removeItem(GAME_STORAGE_KEY);
    localStorage.removeItem(GAME_ID_KEY);
    localStorage.removeItem(MODE_STORAGE_KEY);
    clearPersistedEventCaches();
  } catch (error) {
    logger.error('GAME_PERSISTENCE_CLEAR_FAILED', error);
  }
}

export function clearPersistedEventCaches(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(EVENTS_CACHE_PREFIX)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch (error) {
    logger.error('GAME_PERSISTENCE_CLEAR_EVENTS_CACHE_FAILED', error);
  }
}

export function clearPerGameKeys(gameId?: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith(EVENTS_CACHE_PREFIX) || key.startsWith(PLAYER_ID_PREFIX)) {
        if (!gameId || key.toUpperCase().endsWith(gameId.toUpperCase())) {
          toRemove.push(key);
        }
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    if (!gameId) {
      localStorage.removeItem(LAST_ONLINE_GAME_KEY);
    } else {
      const lastOnline = localStorage.getItem(LAST_ONLINE_GAME_KEY);
      if (lastOnline && lastOnline.toUpperCase() === gameId.toUpperCase()) {
        localStorage.removeItem(LAST_ONLINE_GAME_KEY);
      }
    }
  } catch (error) {
    logger.error('GAME_PERSISTENCE_CLEAR_PER_GAME_KEYS_FAILED', error);
  }
}

// Usuwa wszystkie klucze per-game poza wskazanym (case-insensitive); bez gameId czyści wszystko.
function prunePerGameKeys(activeGameId?: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const keep = activeGameId ? activeGameId.toUpperCase() : null;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const isPerGame = PER_GAME_PREFIXES.some((prefix) => key.startsWith(prefix));
      if (!isPerGame) continue;
      if (!keep || !key.toUpperCase().endsWith(keep)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    if (!keep) {
      localStorage.removeItem(LAST_ONLINE_GAME_KEY);
    }
  } catch (error) {
    logger.error('GAME_PERSISTENCE_PRUNE_FAILED', error);
  }
}

// Usunięto mechanizm migracji – stan jest używany wprost
